import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { getNotifications, markAllNotificationsRead, markNotificationRead } from "../api/notificationService";

const POLL_INTERVAL_MS = 45000;

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);

  const refresh = useCallback(() => {
    if (!isAuthenticated) {
      setItems([]);
      return Promise.resolve();
    }
    return getNotifications()
      .then(setItems)
      .catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
    if (!isAuthenticated) return;
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated, refresh]);

  const unreadCount = items.filter((n) => !n.isRead).length;

  async function markRead(notificationId) {
    setItems((prev) => prev.map((n) => (n.notificationId === notificationId ? { ...n, isRead: true } : n)));
    try {
      await markNotificationRead(notificationId);
    } catch {
      refresh();
    }
  }

  async function markAllRead() {
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    try {
      await markAllNotificationsRead();
    } catch {
      refresh();
    }
  }

  const value = { items, unreadCount, refresh, markRead, markAllRead };

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationsProvider");
  }
  return context;
}
